import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  TextField,
  Button,
  Stack,
  Typography,
  Grid,
} from "@mui/material";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import * as XLSX from "xlsx";
import { useSelector } from "react-redux";

const VisitorModal = ({ open, onClose, initialData, onSave, userRole, parentCount, loading, visitors }) => {
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const { userDetails } = useSelector((state) => state.Login);

  // Define max limit based on user role
  const maxLimit = (userDetails?.user_role === 1 || userDetails?.user_role === 2) ? 50 : 50;
  const allowedToAdd = maxLimit - parentCount;   // remaining rows user can add


  useEffect(() => {
    if (initialData && Array.isArray(initialData)) {
      //('init data', initialData);
      setRows(initialData);
      setErrors(initialData.map(() => ({ name: "", email: "", phone: "", visitor_company : ""})));
    } else {
      //('No initial data, setting default row');
      setRows([{ id: Date.now(), name: "", email: "", phone: "", visitor_company : "" }]);
      setErrors([{ name: "", email: "", phone: "", visitor_company : "" }]);
    }
  }, [initialData, parentCount]);

  const updateField = (index, key, value) => {
    const updated = [...rows];
    updated[index][key] = value;
    setRows(updated);

    const errCopy = [...errors];
    errCopy[index][key] = "";
    setErrors(errCopy);
  };

  const addRow = () => {
    if (rows.length >= allowedToAdd) {
      alert(`You can add only ${allowedToAdd} rows.`);
      return;
    }

    setRows([...rows, { id: Date.now(), name: "", email: "", phone: "", visitor_company : "" }]);
    setErrors([...errors, { name: "", email: "", phone: "", visitor_company : "" }]);
  };

  const removeRow = (index) => {
    setRows(rows.filter((_, i) => i !== index));
    setErrors(errors.filter((_, i) => i !== index));
  };

  const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validate = () => {
  let valid = true;
  const newErr = rows.map(() => ({ name: "", email: "", phone: "", visitor_company : "" }));

  const emailCount = {};

  // Existing invited emails (from API)
  const existingEmails = new Set(
    (visitors?.visitor_personal_details || []).map(v =>
      v.email.trim().toLowerCase()
    )
  );

  //(existingEmails, 'exis')

  rows.forEach((r, idx) => {
    const name = r.name.trim();
    const emailLower = r.email.trim().toLowerCase();

    // Required fields
    if (!name) {
      newErr[idx].name = "Name is required";
      valid = false;
    }

    if (!emailLower) {
      newErr[idx].email = "Email is required";
      valid = false;
      return;
    }

    if (!isValidEmail(emailLower)) {
      newErr[idx].email = "Enter a valid email address";
      valid = false;
      return;
    }

    // Check already invited email
    if (existingEmails.has(emailLower)) {
      newErr[idx].email = "Email already invited";
      valid = false;
      return;
    }

    // Count for duplicate check inside rows
    emailCount[emailLower] = (emailCount[emailLower] || 0) + 1;
  });

  // Mark duplicates within rows
  rows.forEach((r, idx) => {
    const emailLower = r.email.trim().toLowerCase();

    if (emailLower && emailCount[emailLower] > 1) {
      newErr[idx].email = "Duplicate email not allowed";
      valid = false;
    }
  });

  setErrors(newErr);
  return valid;
};

  const handleSubmit = () => {
    if (!validate()) return;
    const updatedRows = rows.map((data) => ({
    ...data,
    phone: data.phone && !data.phone.startsWith("+")
      ? `+${data.phone}`
      : data.phone,
  }));

  onSave(updatedRows);
  };

  //(rows.length, allowedToAdd);

  // 📌 Excel Upload Handler
 const handleExcelUpload = (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  const reader = new FileReader();

  reader.onload = (event) => {
    const data = new Uint8Array(event.target.result);
    const workbook = XLSX.read(data, { type: "array" });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const parsedData = XLSX.utils.sheet_to_json(sheet);

    let excelRows = [];

    parsedData.forEach((item) => {
      if (item.Name && item.Email) {
        excelRows.push({
          id: Date.now() + Math.random(),
          name: item.Name || "",
          email: item.Email || "",
          phone: item.Phone ? String(item.Phone) : "",
        });
      }
    });

    // -------------------------------
    // STEP 1: how many we can still add?
    // -------------------------------
    let remainingSlots = allowedToAdd - rows.length;

    // Check if existing rows are filled or not
const hasFilledRow = rows.some(r => 
  (r.name && r.name.trim() !== "") ||
  (r.email && r.email.trim() !== "") ||
  (r.phone && r.phone.trim() !== "")
);

// If no filled rows exist (first row empty), allow one extra
if (!hasFilledRow) {
  remainingSlots = remainingSlots + 1;
}

    if (remainingSlots <= 0) {
      alert(`You cannot add more than ${allowedToAdd} visitors.`);
      return;
    }

    // Trim excel rows if more than allowed
    if (excelRows.length > remainingSlots) {
      excelRows = excelRows.slice(0, remainingSlots);
    }

    // -------------------------------
    // STEP 2: Fill empty rows first
    // -------------------------------
    let updatedRows = [...rows];
    let updatedErrors = [...errors];

    let excelIndex = 0;

    for (let i = 0; i < updatedRows.length && excelIndex < excelRows.length; i++) {
      const r = updatedRows[i];

      let isEmpty =
        (!r.name || r.name.trim() === "") &&
        (!r.email || r.email.trim() === "") &&
        (!r.phone || r.phone.trim() === "");

      if (isEmpty) {
        updatedRows[i] = excelRows[excelIndex];
        updatedErrors[i] = { name: "", email: "", phone: "" };
        excelIndex++;
      }
    }

    // -------------------------------
    // STEP 3: Add remaining new rows
    // -------------------------------
    const remainingExcelRows = excelRows.slice(excelIndex);

    updatedRows = [...updatedRows, ...remainingExcelRows];
    updatedErrors = [
      ...updatedErrors,
      ...remainingExcelRows.map(() => ({ name: "", email: "", phone: "" })),
    ];

    // -------------------------------
    // Save final state
    // -------------------------------
    setRows(updatedRows);
    setErrors(updatedErrors);
  };

  reader.readAsArrayBuffer(file);
  e.target.value = ""; // reset file input
};

  return (
    <Modal  open={open} onClose={(event, reason) => {
    if (reason === "backdropClick") return;
    onClose();
  }}>
      <Box
        sx={{
          width: "90%",
          background: "white",
          p: 3,
          borderRadius: "10px",
          mx: "auto",
          mt: "5%",
          maxHeight: "80vh",
          overflowY: "auto",
        }}
      >
        <Typography variant="h6" mb={2}>
          {initialData ? "Edit Visitors" : "Add Visitors"}
        </Typography>

        {/* ✔ Excel Upload Button */}
        {/* {userDetails?.user_role !== 5 && (
        <Button variant="contained" component="label" sx={{ mb: 2 }}>
          Upload Excel
          <input hidden type="file" accept=".xlsx" onChange={handleExcelUpload} />
        </Button>
        )} */}
        

        <Typography sx={{ fontSize: "12px", mb: 2 }}>
          {/* {userDetails?.user_role !== 5 && (
            <>
          <b>Excel Columns Required:</b> Name | Email | Phone
          <br />
          </>
          )} */}
          
          <b>Max rows allowed:</b> {allowedToAdd}
          
        </Typography>
<br />
        {rows.map((row, index) => (
          <Grid
            container
            key={row.id}
            spacing={2}
            sx={{
              backgroundColor: "white",
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              p: 2,
              mb: 2,
            }}
          >
            {/* NAME */}
            <Grid item xs={12} sm={3}>
              <Typography>{index + 1}. Name as per ID*</Typography>

              <TextField
                value={row.name}
                onChange={(e) => updateField(index, "name", e.target.value)}
                fullWidth
                inputProps={{ maxLength: 150 }}
                InputProps={{ disableUnderline: true }}
                sx={{
                  "& .MuiInputBase-root": {
                    color: errors[index]?.name ? "#912018" : "#111927",
                  },
                  backgroundColor: errors[index]?.name ? "#FEE4E2" : "#F9FAFB",
                  mt: 1,
                }}
              />
              <Typography sx={{ color: "#B42318", fontSize: "12px" }}>
                {errors[index]?.name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography>Visitor Company</Typography>

              <TextField
                value={row.visitor_company}
                onChange={(e) => updateField(index, "visitor_company", e.target.value)}
                fullWidth
                inputProps={{ maxLength: 200 }}
                InputProps={{ disableUnderline: true }}
                sx={{
                  "& .MuiInputBase-root": {
                    color: errors[index]?.visitor_company ? "#912018" : "#111927",
                  },
                  backgroundColor: errors[index]?.visitor_company ? "#FEE4E2" : "#F9FAFB",
                  mt: 1,
                }}
              />
              <Typography sx={{ color: "#B42318", fontSize: "12px" }}>
                {errors[index]?.visitor_company}
              </Typography>
            </Grid>

            {/* EMAIL */}
            <Grid item xs={12} sm={3}>
              <Typography>Email*</Typography>

              <TextField
                value={row.email}
                onChange={(e) => updateField(index, "email", e.target.value)}
                fullWidth
                inputProps={{ maxLength: 100 }}
                InputProps={{ disableUnderline: true }}
                sx={{
                  "& .MuiInputBase-root": {
                    color: errors[index]?.email ? "#912018" : "#111927",
                  },
                  backgroundColor: errors[index]?.email ? "#FEE4E2" : "#F9FAFB",
                  mt: 1,
                }}
              />
              <Typography sx={{ color: "#B42318", fontSize: "12px" }}>
                {errors[index]?.email}
              </Typography>
            </Grid>

            {/* PHONE */}
            <Grid item xs={12} sm={2}>
              <Typography>Phone</Typography>

              <Box sx={{ mt: 1 }}>
                <PhoneInput
                  country={"sg"}
                  value={row.phone}
                  onChange={(value) => updateField(index, "phone", value)}
                  inputStyle={{
                    backgroundColor: errors[index]?.phone ? "#FEE4E2" : "#F9FAFB",
                    border: "none",
                    height: "46px",
                    width: "100%",
                    borderRadius: "8px",
                    color: errors[index]?.phone ? "#912018" : "#111927",
                  }}
                  buttonStyle={{ border: "none" }}
                  countryCodeEditable={false}
                />
              </Box>
              <Typography sx={{ color: "#B42318", fontSize: "12px" }}>
                {errors[index]?.phone}
              </Typography>
            </Grid>

            {/* ADD / REMOVE */}
            <Grid
              item
              xs={12}
              sm={1}
              sx={{ display: "flex", alignItems: "center", justifyContent: "center", mt: 3 }}
            >
              {rows.length > 1 && (
                <Button onClick={() => removeRow(index)}>
                  <RemoveCircleOutlineIcon color="error" />
                </Button>
              )}

              {index === rows.length - 1 && rows.length < allowedToAdd && (
                <Button onClick={addRow}>
                  <AddCircleOutlineIcon color="primary" />
                </Button>
              )}
            </Grid>
          </Grid>
        ))}

        <Stack direction="row" justifyContent="flex-end" gap={2} mt={2}>
          
          <Button variant="outlined" onClick={() => {
            onClose();
            setRows([{ id: Date.now(), name: "", email: "", phone: "", visitor_company : "" }]);
            setErrors([{ name: "", email: "", phone: "", visitor_company : "" }]);
            }}>
            Close
          </Button>
          <Button disabled={loading} variant="contained" onClick={handleSubmit}>
            Save
          </Button>
          {/* <Button variant="outlined" onClick={() =>{
            setRows([{ id: Date.now(), name: "", email: "", phone: "" }]);
            setErrors([{ name: "", email: "", phone: "" }]);
          }}>
            Cancel
          </Button> */}
        </Stack>
      </Box>
    </Modal>
  );
};

export default VisitorModal;
